import { action, computed, makeObservable, observable } from 'mobx';
import { DEBOUNCE_TIME } from 'src/constants/main';
import { branchStore } from 'src/mobx/branchStore';
import client from 'src/services/client';
import { IBranch } from 'src/types/branches';
import { TBooleanInNumber } from 'src/types/main';
import { IDsp, IProfile } from 'src/types/profiles';
import { arrayMerge } from 'src/utils/formatter';
import { booleanToNumber } from 'src/utils/helpers';
import { debounce } from 'src/utils/perfomance';
import { IBranchesModalItem } from 'src/view/containers/Modals/BranchesModal/BranchesModal';
import logger from '~/services/logger';
import { TProfile } from '~/view/containers/ProfilesList';

class DispatchingRulesStore {
  constructor() {
    makeObservable(this);
  }

  @observable loading: boolean = false;
  @observable profiles: IProfile[] = [];
  @observable selectedProfile: IProfile | null = null;
  @observable selectedBranch: IBranch | null = null;
  @observable selectedDsp: IDsp | null = null;

  @computed get dspForRender(): IDsp[] {
    if (!this.selectedProfile || !this.selectedProfile.default?.dsp.length) return [];

    return this.selectedProfile.default.dsp
      .slice()
      .filter((dsp) => dsp.available && dsp.is_working !== 0)
      .sort((a, b) => a.priority - b.priority);
  }

  @computed get isSelectedDspIndex(): number {
    if (!this.selectedDsp) return 0;
    return this.dspForRender.indexOf(this.selectedDsp);
  }

  @action getProfiles = async () => {
    try {
      this.loading = true;

      const response: IProfile[] = await client.get(`/v3/dispatching-rules/profiles`);

      this.profiles = response;
      if (!this.selectedProfile) this.selectProfile(this.profiles[0]);
      this.loading = false;
    } catch (error: any) {
      this.loading = false;
      logger.error({ error, place: 'DispatchingRulesStore getProfiles', showModal: true });
    }
  };

  @action selectProfile = (profile: TProfile | null) => {
    const selectableProfile: IProfile | null = profile ? this.profiles.find((_profile) => _profile.id === profile.id) || null : null;

    this.selectedProfile = selectableProfile;
    this.driverTimeout = selectableProfile?.default?.dsp[0].interval;
  };

  private scrollToProfile = (id: number) => {
    const profile = document.getElementById(`profile_${id}`);

    if (!profile) return;
    profile.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };

  @action addProfile = async () => {
    try {
      if (!this.selectedProfile) return;
      this.loading = true;
      const response: IProfile = await client.post(`/v3/dispatching-rules/profiles`);
      this.profiles.push(response);
      this.selectProfile(response);
      this.scrollToProfile(response.id);
      this.loading = false;
    } catch (error: any) {
      this.loading = false;
      logger.error({ error, place: 'DispatchingRulesStore addProfile', showModal: true });
    }
  };

  @action deleteProfile = async (id: number) => {
    try {
      if (!this.selectedProfile) return;
      this.loading = true;
      await client.delete(`/v3/dispatching-rules/profiles/${id}`);
      this.profiles = this.profiles.filter((profile) => profile.id !== id);
      this.selectProfile(this.profiles[0]);
      this.scrollToProfile(this.profiles[0].id);
      this.loading = false;
    } catch (error: any) {
      this.loading = false;
      logger.error({ error, place: 'DispatchingRulesStore deleteProfile', showModal: true });
    }
  };

  @action updateProfile = async (params: {
    name?: { id: number; value: string };
    driver_timeout?: number;
    use_dat?: TBooleanInNumber;
    dspList?: IDsp[];
    dspEnabled?: { id: number };
    priority?: { startId: string; overId: string };
  }): Promise<boolean | undefined> => {
    try {
      if (!this.selectedProfile?.default) return;

      let _dspList: IDsp[] | undefined = undefined;
      if (params.driver_timeout) {
        _dspList = this.selectedProfile.default.dsp;
      } else if (params.dspList) {
        _dspList = params.dspList;
      } else if (params.dspEnabled) {
        _dspList = this.changeDspStatus(params.dspEnabled.id);
      } else if (params.priority) {
        _dspList = this.changeDspPriority(params.priority.startId, params.priority.overId);
      }

      this.loading = true;

      const response = await client.put(`/v3/dispatching-rules/profiles/${params.name?.id || this.selectedProfile.id}`, {
        config_id: this.selectedProfile.default.id,
        name: params.name?.value.toString(),
        driver_timeout: params.driver_timeout || this.selectedProfile?.default?.dsp[0].interval,
        dspList: _dspList ? _dspList.map((item) => this.formatDsp(item)) : undefined,
        use_dat: params.use_dat,
      });

      // if changes name, responce === IProfile
      // if (params.name) {
      //   this.profiles = this.profiles.map((item) => (item.id === response.id ? response : item));
      //   this.selectedProfile = response;
      // }
      // // if changes everything else, responce === IDsp
      // else {
      //   this.profiles = this.profiles.map((item) => (item.id === response.profile_id ? this.formatProfile(item, response) : item));
      //   this.selectedProfile = this.formatProfile(this.selectedProfile, response);
      // }
      this.profiles = this.profiles.map((item) => (item.id === response.id ? response : item));
      this.selectedProfile = response;

      this.loading = false;
      return true;
    } catch (error: any) {
      this.loading = false;
      logger.error({ error, place: 'DispatchingRulesStore updateProfile', showModal: true });
    }
  };

  // private formatProfile = (currentProfile: IProfile, responceConfig: IProfileConfig) => {
  //   if (responceConfig.profile_id === currentProfile.id) {
  //     return { ...currentProfile, default: responceConfig };
  //   }
  //   return currentProfile;
  // };

  private formatDsp = (dsp: IDsp) => {
    return { id: dsp.dsp_id, enabled: dsp.enabled, priority: dsp.priority };
  };

  private changeDspStatus = (id: number) => {
    if (!this.selectedProfile?.default) return;

    const changeDsp = (dsp: IDsp) => {
      if (dsp.id === id) {
        return { ...dsp, enabled: booleanToNumber(!dsp.enabled) };
      } else return dsp;
    };
    return this.selectedProfile.default.dsp.slice().map((dsp) => changeDsp(dsp));
  };

  private changeDspPriority = (startId: string, overId: string) => {
    const overItemPriority: number = dispatchingRulesStore.selectedProfile?.default?.dsp.find((item) => item.id === Number(overId))?.priority || 0;
    const activeItemPriority: number = dispatchingRulesStore.selectedDsp?.priority || 0;
    const changePriority = (item: IDsp) => {
      return { ...item, priority: item.id === Number(overId) ? activeItemPriority : overItemPriority };
    };
    return this.selectedProfile?.default?.dsp.map((item) =>
      item.id === Number(overId) || item.id === Number(startId) ? changePriority(item) : item
    );
  };

  @observable driverTimeout: number | undefined = undefined;

  @action changeDriverTimeout = (timeout: number | undefined) => {
    if (!this.selectedProfile?.default) return;
    this.driverTimeout = timeout;
    if (timeout) this.debounceDriverTimeout();
  };

  private handleDriverTimeout = () => {
    if (!this.selectedProfile?.default || !this.driverTimeout || this.driverTimeout === this.selectedProfile.default.dsp[0].interval) return;
    this.updateProfile({ driver_timeout: this.driverTimeout });
  };

  private debounceDriverTimeout = debounce(this.handleDriverTimeout, DEBOUNCE_TIME.LARGE);

  @action movingBranches = async (branches: IBranchesModalItem[] = [], drag?: { branchId: number; profileId: number }) => {
    try {
      if (!this.selectedProfile) return;
      this.loading = true;

      const branchFormatter = (branch: IBranchesModalItem) => {
        return { id: branch.id, profile_id: this.selectedProfile?.id || 0 };
      };
      const _branches = () => {
        if (!this.selectedProfile) return;
        if (drag) {
          return [{ id: drag.branchId, profile_id: drag.profileId }];
        } else {
          const currentSelectedBranches = this.selectedProfile.branches
            .slice()
            .reduce(
              (sum: { id: number; profile_id: number }[], current: IBranchesModalItem) =>
                sum.concat(
                  branches.find((branch) => branch.id === current.id)
                    ? { id: current.id, profile_id: this.selectedProfile?.id || 0 }
                    : { id: current.id, profile_id: 0 }
                ),
              []
            );
          const newSelectedBranches = branches.map((branch) => branchFormatter(branch));
          return arrayMerge(currentSelectedBranches, newSelectedBranches, 'id');
        }
      };
      const responce = await client.put(`v4/branches`, _branches());
      this.updateProfileBranches(responce, Boolean(drag));
      this.loading = false;
    } catch (error: any) {
      this.loading = false;
      logger.error({ error, place: 'DispatchingRulesStore movingBranches', showModal: true });
    }
  };

  private updateProfileBranches = (_branches: IBranch[], drag?: boolean) => {
    if (!this.selectedProfile) return;
    const selectedProfileHandler = (profile: IProfile) => {
      if (drag) return { ...profile, branches: profile.branches.filter((branch) => !_branches.find((item) => item.id === branch.id)) };
      return { ...profile, branches: _branches.filter((branch) => branch.profile_id === this.selectedProfile?.id) };
    };
    const othersProfilesHandler = (profile: IProfile) => {
      if (drag) return { ...profile, branches: _branches[0].profile_id === profile.id ? profile.branches.concat(_branches) : profile.branches };
      return { ...profile, branches: profile.branches.filter((branch) => !_branches.find((item) => item.id === branch.id)) };
    };
    this.profiles = this.profiles.reduce(
      (sum: IProfile[], current: IProfile) =>
        sum.concat(current.id === this.selectedProfile?.id ? selectedProfileHandler(current) : othersProfilesHandler(current)),
      []
    );
    this.selectedProfile = selectedProfileHandler(this.selectedProfile);
  };

  @action deleteBranch = async (id: number) => {
    try {
      if (!this.selectedProfile) return;
      this.loading = true;
      await client.put(`v4/branches/${id}`, { profile_id: 0 });
      this.selectedProfile.branches = this.selectedProfile.branches.filter((branch) => branch.id !== id);
      this.loading = false;
    } catch (error: any) {
      this.loading = false;
      logger.error({ error, place: 'DispatchingRulesStore deleteBranch', showModal: true });
    }
  };

  // from DragAndDrop
  @action setSelectedBranch = (id: number | null) => {
    if (!this.selectedProfile) return;
    this.selectedBranch = branchStore.branches.find((item) => item.id === id) || null;
  };

  // from DragAndDrop
  @action setSelectedDsp = (id: number | null) => {
    if (id === null) this.selectedDsp = null;
    this.selectedDsp = this.dspForRender.find((dsp) => dsp.id === id) || null;
  };
}

const dispatchingRulesStore = new DispatchingRulesStore();

export { dispatchingRulesStore };
